import {
  Button,
  Checkbox,
  Divider,
  Dropdown,
  Input,
  List,
  Menu,
  notification,
  Space,
  Table,
  TableProps,
  Tag,
  Typography,
} from '@pankod/refine-antd';
import { useNavigation } from '@pankod/refine-core';
import { useSearchParams } from '@pankod/refine-react-router-v6';
import { CustomDatePicker } from 'components';
import CustomTableWithPagination from 'components/table/custom-table';
import dayjs from 'dayjs';
import { useDebounce } from 'hooks';
import { PaginationHandler } from 'interfaces';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ActionModuleFilter, ActivityLog, getActivityLogs } from 'services/activity-log';
import { getActivityMessages } from 'utilities/activity-log';
import MetaData from 'utilities/metadata';
import { capitalize, getDateRangeOfLastXMonths } from 'utilities/shared';
import './styles.css';

const MODULES = [
  'sales',
  'purchase',
  'damage',
  'return',
  'transferStock',
  'client',
  'supplier',
  'variant',
  'product',
  'warehouse',
  'user',
  'tag',
  'category',
  'purchasePayment',
  'salesPayment',
];
const ACTIONS = ['create', 'update', 'delete'];
const ALL_FILTERS = MODULES.reduce((filters, item) => {
  ACTIONS.forEach((action) => {
    filters.push(`${item}:${action}`);
  });
  return filters;
}, [] as string[]);

const formatDate = new Intl.DateTimeFormat('en', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

export const ActivityHistory = () => {
  return (
    <List title="Activity History">
      <MetaData />
      <div className="activity-history-toolbar">
        <div className="activity-history-search">
          <SearchFilterDropdown />
        </div>

        <div className="activity-history-date">
          <ActivityDateRange />
        </div>
      </div>

      <ActivityTable />
    </List>
  );
};

const ActivityTable = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [searchParams, _] = useSearchParams();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [totalActivityLogs, setTotalActivityLogs] = useState<number>(0);

  const { replace } = useNavigation();

  const pageSearchParam = searchParams.get('page') ?? '1';
  const limitSearchParam = searchParams.get('limit') ?? '10';

  useEffect(() => {
    (async () => {
      if (Array.from(searchParams.entries()).length === 0) return;

      const startDateSearchParam = searchParams.get('startDate');
      const endDateSearchParam = searchParams.get('endDate');
      const actionModuleSearchParam = searchParams.get('actionModule');
      const labelIdSearchParam = searchParams.get('id');
      const userSearchParam = searchParams.get('user');
      const nameSearchParam = searchParams.get('name');
      let actionModule;
      if (actionModuleSearchParam) {
        const values = actionModuleSearchParam.split(',');
        actionModule = values.reduce((result, item) => {
          const [module, action] = item.split(':');
          result.push({ moduleName: module, action });
          return result;
        }, [] as ActionModuleFilter[]);
      }

      const query = {
        startDate: startDateSearchParam
          ? new Date(`${startDateSearchParam} 00:00:00`).toISOString()
          : undefined,
        endDate: endDateSearchParam
          ? new Date(`${endDateSearchParam} 23:59:59`).toISOString()
          : undefined,
        actionModule,
        labelId: labelIdSearchParam ?? undefined,
        userEmail: userSearchParam ?? undefined,
        start: +pageSearchParam,
        limit: +limitSearchParam,
        name: nameSearchParam ?? undefined,
      };

      try {
        setIsLoading(true);
        const response = await getActivityLogs(query);
        setActivityLogs(response.data);
        setTotalActivityLogs(response.total);
      } catch (err: any) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      } finally {
        setIsLoading(false);
      }
    })();
  }, [limitSearchParam, pageSearchParam, searchParams]);

  const onTableChange: TableProps<ActivityLog>['onChange'] = async (
    pagination,
    _filters,
    _sorter,
    _extra
  ) => {
    const searchParams = new URL(document.location.toString()).searchParams;
    const page = pagination.current ?? 1;
    const limit = pagination.pageSize ?? 10;
    searchParams.set('page', page.toString());
    searchParams.set('limit', limit.toString());
    replace(`/activity-history?${searchParams.toString()}`);
  };

  const onPageChange: PaginationHandler = async (page: number, pageSize: number) => {
    searchParams.set('page', page.toString());
    searchParams.set('limit', pageSize.toString());
    replace(`/activity-history?${searchParams.toString()}`);
  };

  return (
    <CustomTableWithPagination
      total={totalActivityLogs}
      page={Number(searchParams.get('page') ?? 1)}
      limit={Number(searchParams.get('limit') ?? 10)}
      onPageChange={onPageChange}
    >
      <Table
        dataSource={activityLogs}
        loading={isLoading}
        rowKey="id"
        scroll={{ x: true }}
        onChange={onTableChange}
        // pagination={{
        //   defaultPageSize: 10,
        //   showSizeChanger: true,
        //   total: totalActivityLogs,
        //   current: +pageSearchParam,
        //   pageSize: +limitSearchParam,
        // }}
        pagination={false}
      >
        <Table.Column dataIndex="user_email" title="User" />
        <Table.Column<ActivityLog>
          dataIndex={['source', 'action']}
          title="Action"
          render={(_, record): React.ReactNode => {
            const { action } = record;
            let tagColor = 'magenta';
            if (action === 'create') tagColor = 'blue';
            if (action === 'update') tagColor = 'green';

            return (
              <Tag color={tagColor} key={record.id}>
                {action.toUpperCase()}
              </Tag>
            );
          }}
        />
        <Table.Column<ActivityLog>
          dataIndex="module_name"
          title="Module"
          render={(_, record): React.ReactNode => {
            return <Typography.Text>{record.module_name.toUpperCase()}</Typography.Text>;
          }}
        />
        <Table.Column<ActivityLog>
          dataIndex="name"
          title="Name"
          render={(_, record): React.ReactNode => {
            return <Typography.Text>{record.name?.toUpperCase() ?? 'N/A'}</Typography.Text>;
          }}
        />
        <Table.Column<ActivityLog> dataIndex="record_id" title="ID" />
        <Table.Column<ActivityLog>
          dataIndex="data"
          title="Activity"
          width="30%"
          render={(_, record): React.ReactNode => {
            return (
              <ul style={{ display: 'flex', flexDirection: 'column' }}>
                {getActivityMessages(record).map((item) => (
                  <li>{item}</li>
                ))}
              </ul>
            );
          }}
        />
        <Table.Column<ActivityLog>
          dataIndex="created_at"
          title="Date"
          render={(_, record): React.ReactNode => {
            const formattedDate = dayjs(record.created_at).format('MMMM D, YYYY');
            const formattedTime = dayjs(record.created_at).format('h:mm a');
            return <Typography.Text>{`${formattedDate} at ${formattedTime}`}</Typography.Text>;
          }}
        />
      </Table>
    </CustomTableWithPagination>
  );
};

const ActivityDateRange = () => {
  const [dateRange, setDateRange] = useState<[Date, Date]>(getDateRangeOfLastXMonths(1));
  const { replace } = useNavigation();

  const [startDate, endDate] = dateRange;

  useEffect(() => {
    const searchParams = new URL(document.location.toString()).searchParams;
    if (startDate && endDate) {
      searchParams.set('startDate', formatDate.format(startDate).split('/').join('-'));
      searchParams.set('endDate', formatDate.format(endDate).split('/').join('-'));
    }

    replace(`/activity-history?${searchParams.toString()}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  return (
    <CustomDatePicker
      onChange={(values) => {
        if (values && values[0] && values[1]) {
          setDateRange([new Date(values[0]), new Date(values[1])]);
        }
      }}
      format={'DD-MM-YYYY'}
      customDisabledDate={(date: dayjs.Dayjs) => date > dayjs()}
      value={[startDate, endDate]}
    />
  );
};

const SearchFilterDropdown = () => {
  const [searchIdText, setSearchIdText] = useState<string>('');
  const [searchUserText, setSearchUserText] = useState<string>('');
  const [searchNameText, setSearchNameText] = useState<string>('');
  const [selectedFilters, setSelectedFilters] = useState<string[]>(() => {
    const searchParams = new URL(document.location.toString()).searchParams;
    const actionModuleSearchParam = searchParams.get('actionModule');
    return actionModuleSearchParam ? actionModuleSearchParam.split(',') : [];
  });

  const { replace } = useNavigation();
  const debouncedSearchIdText = useDebounce(searchIdText, 300);
  const debouncedSearchUserText = useDebounce(searchUserText, 300);
  const debouncedSearchNameText = useDebounce(searchNameText, 300);

  useEffect(() => {
    const url = new URL(document.location.toString());
    const params = url.searchParams;

    const searchMap = {
      id: debouncedSearchIdText,
      user: debouncedSearchUserText,
      name: debouncedSearchNameText,
    };

    let changed = false;
    Object.entries(searchMap).forEach(([key, value]) => {
      const existing = params.get(key);
      if (value && existing !== value) {
        params.set(key, value);
        changed = true;
      }
      if (!value && existing !== null) {
        params.delete(key);
        changed = true;
      }
    });
    if (changed) {
      replace(`/activity-history?${params.toString()}`);
    }
  }, [debouncedSearchIdText, debouncedSearchUserText, debouncedSearchNameText, replace]);

  const handleTagClose = (filter: string) => {
    setSelectedFilters((prev) => prev.filter((item) => item !== filter));
  };

  const filterTags = selectedFilters.map((filter) => {
    const [module, action] = filter.split(':');
    let tagColor = 'magenta';
    if (action === 'create') tagColor = 'blue';
    if (action === 'update') tagColor = 'green';

    return (
      <Tag color={tagColor} key={filter} closable onClose={() => handleTagClose(filter)}>
        {capitalize(module)} ({capitalize(action)})
      </Tag>
    );
  });

  return (
    <div className="activity-history-filter-group">
      <div className="activity-history-search-row">
        <Input.Group compact className="activity-history-input-group" style={{ display: 'flex' }}>
          <Input
            placeholder="Search by ID"
            value={searchIdText}
            onChange={(e) => setSearchIdText(e.target.value)}
          />
          <Input
            placeholder="Search by User"
            value={searchUserText}
            onChange={(e) => setSearchUserText(e.target.value)}
          />
          <Input
            placeholder="Search by Name"
            value={searchNameText}
            onChange={(e) => setSearchNameText(e.target.value)}
          />
        </Input.Group>

        <FilterDropdown selectedFilters={selectedFilters} setSelectedFilters={setSelectedFilters} />
      </div>

      <div style={{ marginTop: '1rem', display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
        {filterTags}
      </div>
    </div>
  );
};

interface FilterDropdownProps {
  selectedFilters: string[];
  setSelectedFilters: (filters: string[]) => void;
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({ selectedFilters, setSelectedFilters }) => {
  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);
  const [filtersSearchText, setFiltersSearchText] = useState<string>('');
  const [filters, setFilters] = useState<string[]>(selectedFilters);

  const { replace } = useNavigation();

  useEffect(() => {
    setFilters(selectedFilters);
    const searchParams = new URL(document.location.toString()).searchParams;
    if (selectedFilters.length) {
      searchParams.set('actionModule', selectedFilters.join(','));
    } else {
      searchParams.delete('actionModule');
    }

    replace(`/activity-history?${searchParams.toString()}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters]);

  const handleFilterOk = useCallback(() => {
    setIsDropdownVisible(false);
    setSelectedFilters(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleFilterReset = useCallback(() => {
    setFilters([]);
    setSelectedFilters([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const menu = useMemo(() => {
    let filteredModules: string[] = [];
    let filteredActions: string[] = [];

    if (MODULES.some((module) => module.includes(filtersSearchText))) {
      filteredModules = MODULES.filter((module) => module.includes(filtersSearchText));
      filteredActions = ACTIONS;
    } else if (ACTIONS.some((action) => action.includes(filtersSearchText))) {
      filteredModules = MODULES;
      filteredActions = ACTIONS.filter((action) => action.includes(filtersSearchText));
    } else {
      filteredModules = [];
      filteredActions = [];
    }

    const menuOptions = filteredModules.map((module) => {
      return (
        <Menu.ItemGroup key={module} title={capitalize(module)}>
          {filteredActions.map((action) => {
            const filterKey = `${module}:${action}`;
            const isChecked = !!filters.find((filter) => filter === filterKey);

            return (
              <Menu.Item key={filterKey}>
                <Checkbox
                  style={{ width: '100%' }}
                  checked={isChecked}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    if (isChecked) {
                      setFilters((prev) => [...prev, filterKey]);
                    } else {
                      setFilters((prev) => prev.filter((item) => item !== filterKey));
                    }
                  }}
                >
                  {capitalize(action)}
                </Checkbox>
              </Menu.Item>
            );
          })}
        </Menu.ItemGroup>
      );
    });

    const isAllFiltersSelected = filters.length === ALL_FILTERS.length;

    return (
      <div style={{ position: 'relative' }}>
        <Menu multiple style={{ maxHeight: '300px', overflowY: 'scroll' }}>
          <Space style={{ padding: '2px 8px' }}>
            <Input
              type="text"
              placeholder="Search in filter"
              value={filtersSearchText}
              onChange={(e) => setFiltersSearchText(e.target.value)}
            />
          </Space>
          <Divider style={{ margin: '8px 0' }} />

          <Space style={{ padding: '2px 12px' }}>
            <Checkbox
              style={{ width: '100%' }}
              checked={isAllFiltersSelected}
              onChange={(e) => {
                if (e.target.checked) {
                  setFilters(ALL_FILTERS);
                } else {
                  setFilters([]);
                }
              }}
            >
              Select All Item
            </Checkbox>
          </Space>

          {menuOptions}
        </Menu>

        <Divider style={{ margin: 0 }} />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            backgroundColor: '#fff',
            width: '210px',
            position: 'absolute',
            bottom: '-36px',
            left: '0',
            padding: '4px 8px',
            boxShadow: '0 9px 28px 8px rgba(0, 0, 0, 0.05)',
            borderRadius: '0 0 5px 5px',
            borderTop: '1px solid rgba(0, 0, 0, 0.06)',
          }}
        >
          <Button type="link" onClick={handleFilterReset} disabled={filters.length === 0}>
            Reset
          </Button>

          <Button type="link" onClick={handleFilterOk}>
            Ok
          </Button>
        </div>
      </div>
    );
  }, [filters, filtersSearchText, handleFilterOk, handleFilterReset]);

  return (
    <Dropdown
      trigger={['click']}
      visible={isDropdownVisible}
      onVisibleChange={() => setIsDropdownVisible((visible) => !visible)}
      overlay={menu}
    >
      <Button className="activity-history-filter-btn">Filters</Button>
    </Dropdown>
  );
};
